import React, { useState, useMemo } from 'react';

// Material UI
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Hook
import useActivePromotions from '../../hooks/promotions/useActivePromotions';
import useCards from '../../hooks/cards/useCards';
import useStyles from './styles';

const DAYS = [
    { display: 'Todos',      value: null          },
    { display: 'Lunes',      value: 'Monday'      },
    { display: 'Martes',     value: 'Tuesday'     },
    { display: 'Miércoles',  value: 'Wednesday'   },
    { display: 'Jueves',     value: 'Thursday'    },
    { display: 'Viernes',    value: 'Friday'      },
    { display: 'Sábado',     value: 'Saturday'    },
    { display: 'Domingo',    value: 'Sunday'      },
];

const DAYS_API = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const getCurrentDayValue = () => DAYS_API[new Date().getDay()];

const DAY_ES = {
    Monday: 'Lunes',
    Tuesday: 'Martes',
    Wednesday: 'Miércoles',
    Thursday: 'Jueves',
    Friday: 'Viernes',
    Saturday: 'Sábado',
    Sunday: 'Domingo',
};
const translateDay = (day) => DAY_ES[day] ?? day;

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day   = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const year  = String(d.getUTCFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};


const ALL_DAYS_VALUES = ['Todos', 'ALL', 'All'];

const PAYMENT_BRAND_COLORS = {
    VISA: '#1A1F71',
    MASTERCARD: '#EB001B',
    MC: '#EB001B',
    AMEX: '#2E77BC',
    MODO: '#6C2EB9',
    NARANJA: '#F7941D',
    NARANJAX: '#F7941D',
};

const brandColorKey = (name) => name.toUpperCase().replace(/[^A-Z]/g, '');

const PromotionCard = ({ promo, classes, cardNamesById }) => {
    const featured = Boolean(promo.featured ?? promo.isFeatured);
    const isAllDays = !promo.validDays || ALL_DAYS_VALUES.includes(promo.validDays);
    const validDaysLabel = isAllDays ? 'TODOS LOS DÍAS' : translateDay(promo.validDays).toUpperCase();

    const paymentBadges = (Array.isArray(promo.cardIds) ? promo.cardIds : [])
        .map(id => cardNamesById[id])
        .filter(Boolean)
        .map(name => ({ key: name, label: name.toUpperCase() }));

    return (
        <div className={`${classes.card} ${featured ? classes.cardFeatured : ''}`}>
            <Typography className={`${classes.validDaysLabel} ${featured ? classes.validDaysLabelFeatured : ''}`}>
                {validDaysLabel}
            </Typography>
            <Typography className={`${classes.entity} ${featured ? classes.entityFeatured : ''}`}>
                {promo.entity}
            </Typography>
            {promo.mainText && (
                <Typography className={`${classes.mainText} ${featured ? classes.mainTextFeatured : ''}`}>
                    {promo.mainText}
                </Typography>
            )}
            {promo.secondaryText && (
                <Typography className={`${classes.secondaryText} ${featured ? classes.secondaryTextFeatured : ''}`}>
                    {promo.secondaryText}
                </Typography>
            )}
            <div className={`${classes.dateRow} ${featured ? classes.dateRowFeatured : ''}`}>
                <CalendarTodayIcon className={`${classes.dateIcon} ${featured ? classes.dateIconFeatured : ''}`} />
                <span>{formatDate(promo.fromDate)} — {formatDate(promo.toDate)}</span>
            </div>
            {promo.legalText && (
                <Typography className={`${classes.legalText} ${featured ? classes.legalTextFeatured : ''}`}>
                    {promo.legalText}
                </Typography>
            )}
            {(paymentBadges.length > 0) && (
                <div className={classes.paymentMethodsRow}>
                    {paymentBadges.map(({ key, label }) => (
                        <span
                            key={key}
                            className={classes.paymentBadge}
                            style={{ backgroundColor: PAYMENT_BRAND_COLORS[brandColorKey(label)] ?? '#555' }}
                        >
                            {label}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const CARDS_FILTERS = { isExport: true };

const ActivePromotions = () => {
    const { classes } = useStyles();
    const [selectedDay, setSelectedDay] = useState(getCurrentDayValue);
    const { promotions, loading } = useActivePromotions(selectedDay);
    const { cards } = useCards({ filters: CARDS_FILTERS });

    const cardNamesById = useMemo(
        () => Object.fromEntries(cards.map(card => [card.id, card.name])),
        [cards]
    );

    return (
        <Paper className={classes.wrapper}>
            <div className={classes.header}>
                <div className={classes.titleRow}>
                    <Typography variant='h6' fontWeight={700}>
                        Promociones vigentes
                    </Typography>
                    {!loading && promotions.length > 0 && (
                        <span className={classes.badge}>{promotions.length} activas</span>
                    )}
                </div>
            </div>

            <div className={classes.dayTabs}>
                {DAYS.map(day => (
                    <Chip
                        key={day.display}
                        label={day.display}
                        size='small'
                        onClick={() => setSelectedDay(day.value)}
                        color={selectedDay === day.value ? 'primary' : 'default'}
                        variant={selectedDay === day.value ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600, cursor: 'pointer' }}
                    />
                ))}
            </div>

            {loading ? (
                <div className={classes.grid}>
                    {[1, 2, 3, 4, 5].map(i => (
                        <Skeleton key={i} variant='rectangular' height={160} sx={{ borderRadius: 2 }} />
                    ))}
                </div>
            ) : promotions.length === 0 ? (
                <Typography className={classes.emptyText}>
                    No hay promociones activas para el día seleccionado.
                </Typography>
            ) : (
                <div className={classes.grid}>
                    {promotions.map((promo, index) => (
                        <PromotionCard
                            key={promo.id ?? index}
                            promo={promo}
                            classes={classes}
                            cardNamesById={cardNamesById}
                        />
                    ))}
                </div>
            )}
        </Paper>
    );
};

export default ActivePromotions;
